import { Box, Checkbox, Radio } from "@mui/material";
import { FC } from "react";
import type { SxProps } from "@mui/material";
import type {
  ActionButtonBaseProps,
  ButtonCurrentValProps,
} from "../../../util/types";
import CommonImageComp from "../CommonImageComp";
import { useThemeContext } from "../../../globalContext/global-context";

interface ChoiceControlProps extends ActionButtonBaseProps {
  currentVal: ButtonCurrentValProps;
  checked?: boolean;
  mode?: "checkbox" | "radio";
  wrapperSx?: SxProps;
  controlSx?: SxProps;
  controlName?: string;
  controlValue?: string | number;
  controlAriaLabel?: string;
  controlRef?: (el: HTMLButtonElement | null) => void;
  showContent?: boolean;
  contentTestId?: string;
}

const ChoiceControl: FC<ChoiceControlProps> = ({
  onClick,
  testId,
  currentVal,
  disabled,
  isSelected,
  checked,
  mode = "checkbox",
  sx,
  wrapperSx,
  controlSx,
  controlName,
  controlValue,
  controlAriaLabel,
  ariaDisabled,
  controlRef,
  showContent = true,
  contentTestId,
  buttonImageStyle,
  adaptiveText,
}) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const Control = mode === "radio" ? Radio : Checkbox;
  const isChecked = checked ?? isSelected;
  const interactionDisabled = disabled || ariaDisabled;
  const handleSelect = () => {
    if (interactionDisabled) {
      return;
    }
    onClick();
  };

  return (
    <Box
      onClick={handleSelect}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: `${theme.tokens.spacing["2"]}px`,
        cursor: disabled ? "default" : "pointer",
        ...wrapperSx,
      }}
    >
      <Control
        onClick={(event) => {
          event.stopPropagation();
          handleSelect();
        }}
        data-testid={testId}
        name={controlName}
        value={controlValue}
        checked={isChecked}
        disabled={disabled}
        aria-disabled={ariaDisabled}
        sx={{
          color: theme.tokens.color.action.primary,
          "&.Mui-checked": {
            color: theme.tokens.color.action.primary,
          },
          "&.Mui-disabled.Mui-checked": {
            color: theme.tokens.color.action.primary,
          },
          ...controlSx,
        }}
        aria-label={controlAriaLabel}
        slotProps={{
          input: {
            "aria-label": controlAriaLabel,
            "aria-disabled": ariaDisabled,
          },
        }}
        ref={controlRef}
      />
      {showContent && (
        <CommonImageComp
          currentVal={currentVal}
          isSelected={isChecked}
          disabled={disabled && !isChecked}
          isCheckbox={true}
          sx={sx}
          textTestId={contentTestId}
          imageStyle={buttonImageStyle}
          adaptiveText={adaptiveText}
        />
      )}
    </Box>
  );
};

export default ChoiceControl;
